import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, SendIcon, XIcon, MessageSquareIcon, PaperclipIcon, ShieldCheckIcon } from 'lucide-react';
import { WidgetConfig, WIDGET_SIZE, WIDGET_RADIUS } from '../../data/widgetConfig';
import { TalabMark as BrandMark } from '../Logo';
import { readableTextOn, rgba } from '../../utils/theme';
interface WidgetPreviewProps {
  widget: WidgetConfig;
  /** Shop name, used in the mock site chrome. */
  siteName: string;
  /** Featured products the widget can surface inline. */
  products?: {
    id: string;
    name: string;
    image: string;
    price: string;
  }[];
  /** 'open' shows the drawer, 'closed' shows only the launcher. */
  state?: 'open' | 'closed';
  device?: 'desktop' | 'mobile';
}

/**
 * The widget as a visitor to the merchant's own website sees it: an edge
 * drawer that slides in from the side of the page, over a neutral stand-in for
 * their site. Talab's identity is plainly visible in the drawer unless the
 * merchant has white-labelled it.
 */
export function WidgetPreview({
  widget,
  siteName,
  products = [],
  state = 'open',
  device = 'desktop'
}: WidgetPreviewProps) {
  const dark = widget.theme === 'dark';
  const fullScreen = widget.size === 'full';
  const width = WIDGET_SIZE[widget.size].w;
  const radius = WIDGET_RADIUS[widget.corner];
  const onPrimary = readableTextOn(widget.primaryColor);
  const onPrimarySoft = rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#1B2333', 0.74);
  const left = widget.position === 'bottom-left';
  const mobile = device === 'mobile';
  const open = state === 'open';
  const surface = dark ? '#111826' : '#FFFFFF';
  const body = dark ? '#0B1220' : '#F7F9FC';
  const text = dark ? '#E7ECF5' : '#1B2333';
  const muted = dark ? 'rgba(231,236,245,0.55)' : 'rgba(27,35,51,0.5)';
  const hairline = dark ? 'rgba(255,255,255,0.1)' : 'rgba(27,35,51,0.08)';
  const branded = widget.showTalabBranding;
  return <div className="relative w-full h-full overflow-hidden bg-white" data-id="element-5109" data-mp="1juzlp650qfxs">
      {/* Neutral stand-in for the merchant's own website */}
      <div className="absolute inset-0 p-6 lg:p-10" aria-hidden data-id="element-5110" data-mp="1vz3w0z6zp4ku">
        <div className="flex items-center gap-3 mb-8" data-id="element-5111" data-mp="1vxaolhojygbs">
          <div className="w-8 h-8 rounded-lg bg-slate-200" data-id="element-5112" data-mp="20y6uu1wdmekd" />
          <div className="h-2.5 w-28 rounded-full bg-slate-200" data-id="element-5113" data-mp="25p6nuw2dkchl" />
          <div className="ml-auto hidden sm:flex items-center gap-5" data-id="element-5114" data-mp="25qrhmjgv7ree">
            {[48, 40, 56, 36].map(w => <div key={w} className="h-2 rounded-full bg-slate-200/70" style={{
            width: w
          }} data-id="element-5115" data-mp="2aqcgktw1bzr4" />)}
          </div>
        </div>
        <div className="h-3 w-1/3 rounded-full bg-slate-200 mb-3" data-id="element-5116" data-mp="2onst8n4xcprb" />
        <div className="h-7 w-3/5 rounded-lg bg-slate-200/90 mb-3" data-id="element-5117" data-mp="2olycll0orjt1" />
        <div className="h-7 w-2/5 rounded-lg bg-slate-200/60 mb-6" data-id="element-5118" data-mp="pc8buq5n93th" />
        <div className="h-2.5 w-1/2 rounded-full bg-slate-200/60 mb-2" data-id="element-5119" data-mp="u9i3mcgi9rkh" />
        <div className="h-2.5 w-2/5 rounded-full bg-slate-200/60 mb-8" data-id="element-5120" data-mp="mqihhz1ntlxf" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-5121" data-mp="mrtmg26uczq4">
          {[0, 1, 2].map(i => <div key={i} className="rounded-2xl border border-slate-200/80 overflow-hidden" data-id="element-5122" data-mp="hyh76io1y5a1">
              <div className="aspect-[4/3] bg-slate-200/60" data-id="element-5123" data-mp="81us6aieq8fx" />
              <div className="p-3 space-y-2" data-id="element-5124" data-mp="83fkdth91fam">
                <div className="h-2.5 w-3/4 rounded-full bg-slate-200/80" data-id="element-5125" data-mp="15pweyqylleso" />
                <div className="h-2 w-1/2 rounded-full bg-slate-200/50" data-id="element-5126" data-mp="10qa2wkuia4lg" />
              </div>
            </div>)}
        </div>
        <p className="absolute bottom-4 left-6 lg:left-10 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300" data-id="element-5127" data-mp="vqdvscqihx1o">
          {siteName} website
        </p>
      </div>

      {/* Scrim while the drawer is open */}
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="absolute inset-0 bg-dark-navy/20" data-id="element-5128" data-mp="3gfbgzkh6hv6q" />}

      {/* The drawer — slides in from the page edge, full height */}
      {open && <motion.aside initial={{
      x: left ? -40 : 40,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} transition={{
      duration: 0.26,
      ease: [0.23, 1, 0.32, 1]
    }} className="absolute top-0 bottom-0 flex flex-col overflow-hidden shadow-[0_0_60px_rgba(11,18,32,0.24)]" style={{
      width: mobile || fullScreen ? '100%' : width,
      maxWidth: '100%',
      [left ? 'left' : 'right']: 0,
      background: surface,
      borderLeft: left || fullScreen ? undefined : `1px solid ${hairline}`,
      borderRight: left && !fullScreen ? `1px solid ${hairline}` : undefined,
      borderRadius: fullScreen ? 0 : left ? `0 ${radius}px ${radius}px 0` : `${radius}px 0 0 ${radius}px`
    }} data-id="element-5129" data-mp="uars120ue57l">
          {/* Header */}
          <div className="flex-shrink-0" style={{
        background: widget.primaryColor
      }} data-id="element-5130" data-mp="2tpphs69sp3st">
            {/* Talab identity band — the platform is plainly visible here */}
            {branded && <div className="flex items-center gap-2 px-4 py-2" style={{
          borderBottom: `1px solid ${rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#1B2333', 0.16)}`
        }} data-id="element-5131" data-mp="1x2c6nimtn4eq">
                <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 rounded-full bg-white/95" style={{
            height: 22
          }} data-id="element-5132" data-mp="2604oatg0mo5a">
                  <BrandMark size={16} tone="light" data-id="element-5133" data-mp="3dg2wx47islqd" />
                  <span className="font-display font-bold text-[12px] leading-none text-dark-navy" style={{
              letterSpacing: '-0.035em'
            }} data-id="element-5134" data-mp="3df9xkoaw4yi9">
                    talab
                  </span>
                </span>
                <span className="text-[10px] font-semibold" style={{
            color: onPrimarySoft
          }} data-id="element-5135" data-mp="3s3xtwwsk9c99">
                  Verified business assistant
                </span>
                <ShieldCheckIcon className="w-3.5 h-3.5 ml-auto" style={{
            color: onPrimarySoft
          }} data-id="element-5136" data-mp="34ev9jh6jl5b2" />
              </div>}

            <div className="flex items-center gap-3 px-4 py-3.5" data-id="element-5137" data-mp="joe11yvsxvuq">
              <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-white/95" aria-hidden data-id="element-5138" data-mp="onh4jew4vu0l">
                {widget.avatar ? <img src={widget.avatar} alt="" className="w-full h-full object-contain p-1" data-id="element-5139" data-mp="2489cfag52sr4" /> : <SparklesIcon className="w-4 h-4" style={{
              color: widget.primaryColor
            }} data-id="element-5140" data-mp="1ugc0g1hb3wrc" />}
              </span>
              <div className="min-w-0 flex-1" data-id="element-5141" data-mp="1lijn32v5az6l">
                <p className="text-sm font-bold leading-tight truncate" style={{
              color: onPrimary
            }} data-id="element-5142" data-mp="1ggkhrnfuhs70">
                  {widget.name || 'AI Assistant'}
                </p>
                <p className="text-[11px] leading-tight truncate" style={{
              color: onPrimarySoft
            }} data-id="element-5143" data-mp="3at0sp1jl5ui7">
                  {widget.subtitle}
                </p>
              </div>
              <XIcon className="w-4.5 h-4.5 flex-shrink-0" style={{
            color: onPrimarySoft
          }} data-id="element-5144" data-mp="2jphri7lhz1qi" />
            </div>
          </div>

          {/* Conversation */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3.5 space-y-3" style={{
        background: body
      }} data-id="element-5145" data-mp="1w0gd83pzgwdb">
            <div className="flex gap-2" data-id="element-5146" data-mp="210unztiyykf1">
              <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{
            background: rgba(widget.primaryColor, 0.12)
          }} data-id="element-5147" data-mp="1m2is3p3f7fox">
                <SparklesIcon className="w-3.5 h-3.5" style={{
              color: widget.primaryColor
            }} data-id="element-5148" data-mp="3ghl7gtyr9he4" />
              </span>
              <div className="max-w-[86%] px-3.5 py-2.5 text-[13px] leading-relaxed" style={{
            background: surface,
            color: text,
            border: `1px solid ${hairline}`,
            borderRadius: Math.max(6, radius * 0.6)
          }} data-id="element-5149" data-mp="19skm624dnttv">
                {widget.greeting}
              </div>
            </div>

            {/* Inline product cards */}
            {widget.showProducts && products.length > 0 && <div className="pl-9 grid grid-cols-2 gap-2" data-id="element-5150" data-mp="1oj09bmgctbuo">
                {products.slice(0, 2).map(p => <div key={p.id} className="overflow-hidden" style={{
            background: surface,
            border: `1px solid ${hairline}`,
            borderRadius: Math.max(6, radius * 0.5)
          }} data-id="element-5151" data-mp="22ece6mo84ipb">
                    <img src={p.image} alt="" className="w-full aspect-[4/3] object-cover" data-id="element-5152" data-mp="11rg9ty6itjlk" />
                    <div className="p-2" data-id="element-5153" data-mp="rsqpzzv2dp2o">
                      <p className="text-[11px] font-bold truncate" style={{
                color: text
              }} data-id="element-5154" data-mp="rtip3knve4xp">
                        {p.name}
                      </p>
                      <p className="text-[10px] font-semibold truncate" style={{
                color: widget.primaryColor
              }} data-id="element-5155" data-mp="1fibdmw25xaia">
                        {p.price}
                      </p>
                    </div>
                  </div>)}
              </div>}

            {/* Suggested prompts */}
            <div className="pl-9 flex flex-col items-start gap-1.5" data-id="element-5156" data-mp="1yfw58awban6l">
              {widget.prompts.filter(Boolean).slice(0, 4).map(p => <span key={p} className="text-left text-[12px] font-medium px-3 py-1.5" style={{
            color: text,
            background: surface,
            border: `1px solid ${rgba(widget.accentColor, 0.55)}`,
            borderRadius: 999
          }} data-id="element-5157" data-mp="3m3to6inc1b5t">
                    {p}
                  </span>)}
            </div>

            {/* Lead capture */}
            {widget.captureLeads && <div className="ml-9 p-3" style={{
          background: rgba(widget.accentColor, 0.12),
          border: `1px solid ${rgba(widget.accentColor, 0.4)}`,
          borderRadius: Math.max(6, radius * 0.5)
        }} data-id="element-5158" data-mp="hotu0sqsp6fj">
                <p className="text-[12px] font-semibold mb-2" style={{
            color: text
          }} data-id="element-5159" data-mp="2npmij4yzh6aw">
                  {widget.leadPrompt}
                </p>
                <div className="flex items-center gap-2" data-id="element-5160" data-mp="2xhsc2j4vvvs7">
                  <span className="flex-1 text-[11px] px-2.5 py-1.5" style={{
              background: surface,
              color: muted,
              border: `1px solid ${hairline}`,
              borderRadius: 999
            }} data-id="element-5161" data-mp="2soz7bv9f0ng4">
                    name@company.com
                  </span>
                  <span className="text-[11px] font-bold px-3 py-1.5" style={{
              background: widget.primaryColor,
              color: onPrimary,
              borderRadius: 999
            }} data-id="element-5162" data-mp="3bfgwxqyyb2x9">
                    Send
                  </span>
                </div>
              </div>}
          </div>

          {/* Composer */}
          <div className="flex-shrink-0 px-3 pt-3 pb-2.5" style={{
        background: surface,
        borderTop: `1px solid ${hairline}`
      }} data-id="element-5163" data-mp="1i87hygp93mk">
            <div className="flex items-center gap-2 px-3 py-2" style={{
          background: dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
          borderRadius: 999
        }} data-id="element-5164" data-mp="3nwrp040z0yx9">
              <PaperclipIcon className="w-4 h-4 flex-shrink-0" style={{
            color: muted
          }} data-id="element-5165" data-mp="300vpik037ltd" />
              <span className="flex-1 text-[13px] truncate" style={{
            color: muted
          }} data-id="element-5166" data-mp="351jnxqqnb4xm">
                {widget.placeholder}
              </span>
              <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{
            background: widget.primaryColor,
            color: onPrimary
          }} data-id="element-5167" data-mp="2zb8ab4gx83t5">
                <SendIcon className="w-3.5 h-3.5" data-id="element-5168" data-mp="2qcwodfr01u71" />
              </span>
            </div>
            {branded ? <p className="text-center text-[10px] mt-2 font-medium" style={{
          color: muted
        }} data-id="element-5169" data-mp="2le3b9ofmk57e">
                Powered by{' '}
                <span className="font-bold" style={{
            color: text
          }} data-id="element-5170" data-mp="27k2ircf9nefh">
                  Talab.app
                </span>
              </p> : <p className="text-center text-[10px] mt-2 font-medium" style={{
          color: muted
        }} data-id="element-5171" data-mp="1xktiy2xpz5l7">
                {siteName}
              </p>}
          </div>
        </motion.aside>}

      {/* Launcher — replaced by the drawer itself while open */}
      {!open && <div className={`absolute flex ${left ? 'justify-start' : 'justify-end'}`} style={{
      bottom: mobile ? 12 : widget.offsetY,
      left: mobile ? 12 : left ? widget.offsetX : undefined,
      right: mobile ? 12 : left ? undefined : widget.offsetX
    }} data-id="element-5172" data-mp="1sgrcow1hp6hj">
        {widget.launcher === 'bubble' ? <span className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_28px_rgba(11,18,32,0.22)]" style={{
        background: widget.primaryColor,
        color: onPrimary
      }} data-id="element-5173" data-mp="14rznmdj7548z">
            <MessageSquareIcon className="w-6 h-6" data-id="element-5174" data-mp="h1cexq1giald" />
            {/* Talab mark, seated on the bubble's edge like a verified seal */}
            {branded && <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-[0_2px_6px_rgba(11,18,32,0.18)]" style={{
          border: `2px solid ${widget.primaryColor}`
        }} data-id="element-5175" data-mp="e0995t8pd4zv">
                <TalabMark size={13} data-id="element-5176" data-mp="iqzfb8oydbvm" />
              </span>}
          </span> : widget.launcher === 'pill' ? <span className="inline-flex items-center gap-2.5 pl-4 pr-4 py-3 rounded-full text-sm font-bold shadow-[0_10px_28px_rgba(11,18,32,0.22)]" style={{
        background: widget.primaryColor,
        color: onPrimary
      }} data-id="element-5177" data-mp="3r7kp2rpes8zs">
            <SparklesIcon className="w-4 h-4" style={{
          color: widget.accentColor
        }} data-id="element-5178" data-mp="3w5ckvwt6cf99" />
            {widget.launcherLabel || 'Ask AI'}
            {branded && <>
                <span className="w-px self-stretch my-0.5" style={{
            background: rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#1B2333', 0.28)
          }} data-id="element-5179" data-mp="1izfoe48ng67i" />
                <TalabWordmark height={13} tone={onPrimary} data-id="element-5180" data-mp="1e02ytbqe8l18" />
              </>}
          </span> : <span className="inline-flex items-center gap-2.5 px-5 py-3 text-sm font-bold shadow-[0_10px_28px_rgba(11,18,32,0.22)]" style={{
        background: widget.primaryColor,
        color: onPrimary,
        borderRadius: `${radius}px ${radius}px 0 0`
      }} data-id="element-5181" data-mp="21pnla4lra0f9">
            <SparklesIcon className="w-4 h-4" style={{
          color: widget.accentColor
        }} data-id="element-5182" data-mp="1wxnr6ssda1ql" />
            {widget.launcherLabel || 'Ask AI'}
            {branded && <>
                <span className="w-px self-stretch my-0.5" style={{
            background: rgba(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#1B2333', 0.28)
          }} data-id="element-5183" data-mp="20olqhasxjsya" />
                <TalabWordmark height={13} tone={onPrimary} data-id="element-5184" data-mp="1m0hfkt04clq2" />
              </>}
          </span>}
      </div>}
    </div>;
}

/**
 * The Talab mark on a white seat — the tile reads as the seat itself, so only
 * the bubble-and-spark glyph shows.
 */
function TalabMark({
  size
}: {
  size: number;
}) {
  return <BrandMark size={size} tone="light" className="flex-shrink-0" data-id="element-5185" data-mp="29o8rhvhqywqt" />;
}

/** The wordmark, tinted to match the surface it sits on. */
function TalabWordmark({
  height,
  tone
}: {
  height: number;
  tone: string;
}) {
  const white = tone === '#FFFFFF';
  return <span className="font-display font-bold leading-none flex-shrink-0" style={{
    fontSize: height,
    letterSpacing: '-0.035em',
    color: white ? '#FFFFFF' : '#1B2333',
    opacity: 0.88
  }} data-id="element-5186" data-mp="tba6x16yafr3">
      talab
    </span>;
}